
import React, { useEffect, useRef, useState } from "react";
import "../style/popups.css";
import "../style/system-notice.css";
import API from "../api/api";
import { BASE_URL } from "../utils/constants";
import illo from "../assets/guest-illustration.png";
import { useNavigate } from "react-router-dom";
import { formatRenewalTime, useLanguage } from "../utils/language";

// ═════════════════════════════════════════════════════════════════════════════=
// POPUP 6 — System Notice Popup
// ═════════════════════════════════════════════════════════════════════════════=
export function SystemNoticePopup({ message, onClose }) {
  if (!message) return null;
  return (
    <div className="system-notice-overlay" onPointerDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="system-notice-card">
        <button className="system-notice-close" onClick={onClose}>×</button>
        <h2 className="system-notice-title">System Notice</h2>
        <div className="system-notice-message">{message}</div>
      </div>
    </div>
  );
}

// Build a full icon URL for department icons (handles relative /uploads/... paths)
const deptIconUrl = (icon) => {
  if (!icon) return null;
  if (icon.startsWith("http")) return icon;
  return `${BASE_URL}${icon}`;
};

export const getDepartmentEmoji = (name = "") => {
  const lower = (name || "").toLowerCase();
  if (lower.includes("soil") || lower.includes("मृदा")) return "🌱";
  if (lower.includes("water supply") || lower.includes("sanitation") || lower.includes("पाणीपुरवठा") || lower.includes("स्वच्छता")) return "🚰";
  if (lower.includes("water resources") || lower.includes("जलसंपदा") || lower.includes("water") || lower.includes("जल") || lower.includes("पाणी")) return "💧";
  if (lower.includes("food") || lower.includes("civil supplies") || lower.includes("consumer") || lower.includes("अन्न") || lower.includes("नागरी पुरवठा") || lower.includes("ग्राहक संरक्षण")) return "🛒";
  if (lower.includes("minority") || lower.includes("अल्पसंख्याक")) return "🤝";
  if (lower.includes("tribal") || lower.includes("आदिवासी")) return "🏹";
  if (lower.includes("higher") || lower.includes("technical education") || lower.includes("उच्च व तंत्र")) return "🎓";
  if (lower.includes("skill") || lower.includes("entrepreneurship") || lower.includes("कौशल्य विकास") || lower.includes("उद्योजकता")) return "🛠️";
  if (lower.includes("agri") || lower.includes("dairy") || lower.includes("fisheries") || lower.includes("animal") || lower.includes("शेती") || lower.includes("कृषी") || lower.includes("पशुसंवर्धन") || lower.includes("मत्स्यव्यवसाय")) return "🌾";
  if (lower.includes("rural") || lower.includes("ग्राम")) return "🏡";
  if (lower.includes("home") || lower.includes("police") || lower.includes("गृह") || lower.includes("पोलीस")) return "🛡️";
  if (lower.includes("housing") || lower.includes("गृहनिर्माण")) return "🏠";
  if (lower.includes("divyang") || lower.includes("disabled") || lower.includes("दिव्यांग") || lower.includes("अपंग")) return "♿";
  if (lower.includes("urban") || lower.includes("नगर विकास")) return "🏙️";
  if (lower.includes("planning") || lower.includes("नियोजन")) return "📊";
  if (lower.includes("tourism") || lower.includes("cultural") || lower.includes("पर्यटन") || lower.includes("सांस्कृतिक")) return "🎭";
  if (lower.includes("marathi") || lower.includes("मराठी")) return "📖";
  if (lower.includes("information technology") || lower.includes("it") || lower.includes("माहिती तंत्रज्ञान")) return "💻";
  if (lower.includes("women") || lower.includes("child") || lower.includes("महिला") || lower.includes("बाल")) return "👩‍👧";
  if (lower.includes("revenue") || lower.includes("forest") || lower.includes("महसूल") || lower.includes("वन")) return "🌲";
  if (lower.includes("financ") || lower.includes("वित्त") || lower.includes("अर्थ")) return "💰";
  if (lower.includes("medical") || lower.includes("drug") || lower.includes("वैद्यकीय") || lower.includes("औषधी")) return "💊";
  if (lower.includes("law") || lower.includes("judiciary") || lower.includes("विधि") || lower.includes("न्याय")) return "⚖️";
  if (lower.includes("school") || lower.includes("sports") || lower.includes("शिक्षण") || lower.includes("क्रीडा")) return "📚";
  if (lower.includes("social justice") || lower.includes("special assistance") || lower.includes("सामाजिक न्याय") || lower.includes("विशेष सहाय्य")) return "👥";
  if (lower.includes("general admin") || lower.includes("सामान्य प्रशासन") || lower.includes("administration")) return "📋";
  if (lower.includes("health") || lower.includes("आरोग्य")) return "🏥";
  if (lower.includes("public work") || lower.includes("pwd") || lower.includes("बांधकाम")) return "🏗️";
  if (lower.includes("parliamentary") || lower.includes("संसदीय")) return "🏛️";
  if (lower.includes("co-operation") || lower.includes("textile") || lower.includes("marketing") || lower.includes("सहकार") || lower.includes("पणन") || lower.includes("वस्त्रोद्योग")) return "🧵";
  if (lower.includes("industry") || lower.includes("energy") || lower.includes("labour") || lower.includes("उद्योग") || lower.includes("ऊर्जा") || lower.includes("कामगार")) return "🏭";
  if (lower.includes("environment") || lower.includes("पर्यावरण")) return "🌿";
  if (lower.includes("transport") || lower.includes("वाहतूक")) return "🚌";
  return "🏢";
};

function DeptIconItem({ dept }) {
  const [imgError, setImgError] = useState(false);
  const iconUrl = deptIconUrl(dept?.icon);
  const fallback = getDepartmentEmoji(dept?.name);

  if (!iconUrl || imgError) {
    return (
      <span className="gp-dept-icon-fallback" role="img" aria-label={dept?.name || "Department"}>
        {fallback}
      </span>
    );
  }

  return (
    <img
      src={iconUrl}
      alt={dept?.name || "Department"}
      className="gp-dept-icon"
      onError={() => setImgError(true)}
    />
  );
}

const GUEST_FREE_CHATS = 5;

const getDepartmentShortcut = (name = "") => {
  const words = name
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean);

  if (!words.length) return "";

  if (words.length === 1) {
    return words[0].slice(0, 3).toUpperCase();
  }

  const initials = words.slice(0, 3).map((word) => word[0]).join("");
  return initials.slice(0, 3).toUpperCase();
};

// ─── Departments list ─────────────────────────────────────────────────────────

// ══════════════════════════════════════════════════════════════════════════════
// POPUP 1 — Department Selector
// "Welcome {name}! Select departments you are interested in"
// ══════════════════════════════════════════════════════════════════════════════

const MAX_SELECT = 5;

export function DeptSelector({
  userName = "User",
  onDone = () => { },   // receives (selectedIds, selectedDeptObjects)
  onClose = () => { },
  preSelected,          // ← no default [] here (avoids new-ref-every-render)
}) {
  // ✅ Initialize once from preSelected — no sync useEffect needed
  const [selected, setSelected] = useState(() =>
    Array.isArray(preSelected) ? preSelected : []
  );
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [warningMsg, setWarningMsg] = useState("");

  const fetchDepartments = async () => {
    setLoading(true);
    setFetchError(false);
    try {
      // Fetch all active departments (up to 100)
      const res = await API.get("/departments?active=true&limit=100");
      setDepartments(res.data.data || []);
    } catch (err) {
      console.error("Failed to load departments", err);
      setFetchError(true);
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ FETCH ON MOUNT
  useEffect(() => {
    fetchDepartments();
  }, []); // ← empty dep array: runs once only

  // ✅ TOGGLE SELECT
  const toggle = (id) => {
    setSelected((prev) => {
      if (prev.includes(id)) {
        setWarningMsg("");
        return prev.filter((d) => d !== id);
      }

      if (prev.length >= MAX_SELECT) {
        setWarningMsg(`You can select up to ${MAX_SELECT} preferred departments.`);
        setTimeout(() => setWarningMsg(""), 3000);
        return prev;
      }

      setWarningMsg("");
      return [...prev, id];
    });
  };

  // ✅ FILTER DEPARTMENTS
  const filteredDepartments = departments.filter((d) =>
    (d.name || "").toLowerCase().includes(searchQuery.trim().toLowerCase())
  );

  // ✅ LOADING STATE
  if (loading) {
    return (
      <div className="gp-overlay-dept">
        <div className="gp-card--dept">
          <p style={{ color: "#FFFDFD", textAlign: "center", padding: "40px 0" }}>Loading departments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gp-overlay-dept" onPointerDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="gp-card--dept">
        <button
          type="button"
          className="gp-close-btn"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        {/* HEADER */}
        <div className="gp-dept-header">
          <div className="gp-dept-header-info">
            <h2 className="gp-dept-title">
              Welcome {userName}!
            </h2>

            <p className="gp-dept-sub">
              {departments.length === 0
                ? "Departments are being set up, please try again shortly or contact support"
                : "Select your preferred departments"}
              {departments.length > 0 && (
                <span className="gp-dept-count">
                  {" "}
                  {selected.length}/{MAX_SELECT}
                </span>
              )}
            </p>
          </div>

          {/* SEARCH INPUT */}
          {departments.length > 0 && (
            <div className="gp-dept-search-wrapper">
              <span className="gp-dept-search-icon">🔍</span>
              <input
                type="text"
                className="gp-dept-search-input"
                placeholder="Search from 32 departments..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  type="button"
                  className="gp-dept-search-clear"
                  onClick={() => setSearchQuery("")}
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
          )}
        </div>

        {warningMsg && (
          <div className="gp-dept-warning">
            ⚠️ {warningMsg}
          </div>
        )}

        {/* DEPARTMENTS CONTAINER */}
        <div className="gp-slider-container">
          {departments.length === 0 ? (
            <div className="gp-dept-empty-state" style={{ padding: "30px 20px", textAlign: "center", color: "#FFFDFD", display: "flex", flexDirection: "column", alignItems: "center", gap: "16px" }}>
              <p style={{ margin: 0, fontSize: "15px", lineHeight: "1.5", maxWidth: "480px" }}>
                {fetchError
                  ? "Unable to connect to department services. Please check your connection and try reloading."
                  : "Departments are being set up, please try again shortly or contact support."}
              </p>
              <button
                type="button"
                onClick={fetchDepartments}
                style={{
                  padding: "10px 22px",
                  borderRadius: "22px",
                  border: "1.5px solid #FFFFFF",
                  background: "rgba(255, 255, 255, 0.2)",
                  color: "#FFFFFF",
                  fontWeight: "700",
                  fontSize: "14px",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "all 0.2s ease"
                }}
              >
                <span>🔄</span> Retry Loading
              </button>
            </div>
          ) : filteredDepartments.length === 0 ? (
            <div className="gp-dept-empty-state" style={{ padding: "40px 20px", textAlign: "center", color: "#FFFDFD" }}>
              <p style={{ margin: 0, fontSize: "16px" }}>No departments match "{searchQuery}"</p>
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                style={{
                  marginTop: "12px",
                  padding: "6px 16px",
                  borderRadius: "16px",
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "1px solid #FFF",
                  color: "#FFF",
                  cursor: "pointer",
                  fontSize: "13px"
                }}
              >
                Clear filter
              </button>
            </div>
          ) : (
            <div className="gp-dept-grid-viewport">
              <div className="gp-dept-grid-all">
                {filteredDepartments.map((dept) => {
                  const active = selected.includes(dept._id);

                  return (
                    <div
                      key={dept._id}
                      className={`gp-dept-card ${active ? "gp-dept-card--active" : ""}`}
                      onClick={() => toggle(dept._id)}
                      title={dept.name}
                    >
                      <div className="gp-dept-icon-box">
                        <DeptIconItem dept={dept} />
                      </div>

                      <div className="gp-dept-label-box">
                        <span className="gp-dept-label">
                          {dept.name}
                        </span>
                      </div>

                      {active && (
                        <span className="gp-dept-check">✓</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="gp-dept-footer">
          {selected.length > 0 && (
            <button
              type="button"
              className="gp-btn--clear"
              onClick={() => setSelected([])}
            >
              Clear all
            </button>
          )}
          <button
            className="gp-btn--gold gp-btn--full"
            onClick={() => {
              // ✅ Pass both IDs AND full dept objects so caller can save names
              const selectedDepts = departments.filter((d) => selected.includes(d._id));
              onDone(selected, selectedDepts);
            }}
          >
            {selected.length === 0
              ? "Skip for now →"
              : `Continue with ${selected.length} dept${selected.length > 1 ? "s" : ""} →`}
          </button>
        </div>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// POPUP 2 — Welcome Card
// "Welcome {name} to GRGPT — begin your journey with 5 free chats"
// ══════════════════════════════════════════════════════════════════════════════

export function WelcomeCard({ userName = "User", onClose, freeChatsLeft = 5 }) {
  return (
    <div
      className="wc-overlay"
      onPointerDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="wc-card">

        {/* ───── Banner ───── */}
        <div className="wc-banner">

          {/* SVGs */}
          <div className="wc-illustration">

            {/* LEFT (base shape) */}
            <svg viewBox="0 0 102 82" className="wc-svg-left">
              <path d="M80.7305 2.41406C82.8274 0.00509152 84.7014 -0.271682 85.7529 0.180664C84.6994 0.180802 82.902 0.755988 80.7305 2.41406C78.8594 4.56367 76.8108 8.4111 75.0098 14.9521C70.2351 32.2928 38.0064 36.1466 27.8604 43.8535C17.7146 51.5604 20.1018 61.8365 10.5527 74.0391C8.18002 77.0711 6.82068 79.7273 6.14551 82H0C5.24875 72.5811 16.3958 68.1864 18.3125 52.2031C20.6998 32.2935 64.2677 29.7238 71.4297 14.9521C74.6308 8.3498 77.9807 4.51369 80.7305 2.41406ZM75.0098 56.6982C77.8739 16.1085 88.9356 8.9585 94.1084 10.457C88.3791 21.7607 96.4949 52.4167 101.27 66.332L58.5498 65.5039L18.3125 71.2002L16.5049 82H13.7754L14.7305 68.9014L35.7656 64.0801L94.1084 60.5518L88.1396 48.3496C83.3651 41.6703 77.3971 51.1321 75.0098 56.6982Z" fill="white" />
            </svg>

            {/* RIGHT (main figure) */}

            <svg viewBox="0 0 178 250" className="wc-svg-right">
              <path d="M39.1731 147.823C48.5526 158.836 44.2537 172.437 40.9317 177.86C42.2383 173.271 43.7158 163.843 39.1731 162.842C34.6303 161.841 31.0278 155.75 29.7943 152.83C25.1045 155.833 17.6791 154.081 14.5525 152.83C3.76603 159.338 4.19592 169.308 5.75918 173.48C7.90867 180.155 15.725 192.754 29.7943 189.751C43.8637 186.747 50.1167 186.83 51.4846 187.247C65.5546 193.255 72.9797 228.549 74.9335 245.445L107.792 241.137C105.404 241.139 101.923 237.246 103.658 221.04C106.003 199.137 159.35 174.106 166.971 147.823C173.067 126.796 157.396 79.8214 148.798 58.9626C141.177 63.3431 119.486 63.9689 119.486 60.2145C119.486 56.4602 130.038 56.4595 137.073 51.4536C144.108 46.4477 136.487 45.8213 134.728 32.6799C132.97 19.5385 124.176 3.26823 127.694 16.4096C131.211 29.551 124.176 36.4346 121.831 32.6799C119.486 28.9252 114.21 13.2807 108.348 8.90025C102.486 4.51979 115.969 28.9252 115.969 32.6799C115.969 36.4346 107.762 27.674 103.658 20.1643C99.5549 12.6546 91.934 7.02291 94.8651 15.158C97.7962 23.2932 108.348 37.6861 106.59 42.6924C104.831 47.6986 97.21 38.3119 91.934 42.6924C86.658 47.0728 117.142 60.2145 109.521 73.3556C101.9 86.4966 114.21 99.6383 114.21 127.173C114.21 154.707 52.657 177.86 66.7264 168.474C80.7957 159.088 115.969 143.443 106.003 109.651C96.0375 75.8584 107.176 78.9876 101.314 65.8462C95.4513 52.7048 82.5544 44.5697 79.0371 40.1892C75.5197 35.8088 78.4508 27.674 88.4166 33.9314C96.3893 38.9374 100.336 36.0173 101.314 33.9314C96.0375 28.0908 86.0717 14.0317 88.4166 4.52011C90.2835 -3.05254 99.4581 7.64689 106.474 17.7974C104.04 12.9512 102.174 7.53846 101.314 4.52011C106.472 -9.99812 119.486 13.9067 125.349 27.674C122.027 19.1216 117.376 2.51761 125.349 4.52011C135.314 7.02323 141.763 43.9442 141.763 51.4536C141.763 58.963 111.279 59.5887 125.349 60.2145C136.604 60.7151 147.234 54.5825 151.142 51.4536C158.177 77.9446 173.302 133.43 177.523 143.443C182.799 155.96 142.935 204.144 127.694 214.156C115.5 222.166 120.268 240.856 124.176 249.2H70.8299C73.7611 236.059 62.6228 203.518 55.0019 191.628C47.381 179.738 12.793 207.899 2.24101 174.106C-6.2006 147.073 11.2298 139.062 21.0001 138.436C21.1956 134.264 24.0485 126.922 33.8971 130.927C43.7456 134.932 41.518 143.859 39.1731 147.823Z" fill="white" />
            </svg>
          </div>

          {/* Close */}
          <button className="wc-close" onClick={onClose}>×</button>
        </div>

        {/* ───── Body ───── */}
        <div className="wc-body">

          <div className="wc-check">
            <svg viewBox="0 0 24 24" stroke="white" strokeWidth="3" fill="none">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>

          <h2 className="wc-title">
            Welcome {userName} to GRGPT
          </h2>

          <p className="wc-sub">
            Your first {freeChatsLeft} chats are waiting for you
          </p>
        </div>
      </div>
    </div>
  );
}
// ══════════════════════════════════════════════════════════════════════════════
// POPUP 3 — Guest Auth Popup (auto-shown 2-3s, or "maybe later" flow)
// ══════════════════════════════════════════════════════════════════════════════

export function GuestAuthPopup({ onClose, onNavigate }) {
  const navigate = useNavigate();
  return (
    <div
      className="ga-overlay"
      onPointerDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="ga-card">

        <button className="ga-close" onClick={onClose}>✕</button>

        <div className="ga-banner">

          {/* LEFT */}
          <div className="ga-left">
            <h2 className="ga-title">
              Welcome to GRGPT where every GR feels easier
              to understand
            </h2>

            <p className="ga-sub">
              Explore important GR updates in a simple and clear way.
              Find what matters to your work quickly and without confusion.
            </p>

            {/* BUTTONS MOVED HERE (correct design) */}
            <div className="ga-buttons">
              <button className="ga-btn ga-btn-primary"
                onClick={() => navigate("/login")}>
                Sign In
              </button>

              <button className="ga-btn ga-btn-secondary"
                onClick={() => navigate("/signup")}>
                Sign Up
              </button>

            </div>
          </div>

          {/* RIGHT */}
          <div className="ga-right">
            <img src={illo} alt="" />
          </div>

        </div>

      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// POPUP 4 — Guest Limit Reached (5 free chats used)
// ══════════════════════════════════════════════════════════════════════════════

export function GuestLimitPopup({ onClose, onNavigate }) {
  const [language] = useLanguage();
  const isMarathi = language === "mr";

  return (
    <div
      className="glm-overlay"
      onPointerDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="glm-card" role="dialog" aria-modal="true" aria-labelledby="glm-title">
        <button className="glm-close" onClick={onClose} aria-label="Close dialog">✕</button>

        <div className="glm-banner">
          <div className="glm-icon">🔒</div>
          <h2 id="glm-title" className="glm-title">
            {isMarathi ? "५ मोफत चॅट्स पूर्ण झाले!" : "5 Free Chats Used Up!"}
          </h2>
        </div>

        <div className="glm-body">
          <p className="glm-text">
            {isMarathi
              ? "तुम्ही तुमचे ५ मोफत गेस्ट चॅट्स वापरले आहेत. पुढे संभाषण सुरू ठेवण्यासाठी कृपया लॉगिन करा किंवा नवीन खाते तयार करा."
              : "You've used all 5 of your free guest chats. Please sign in or register to unlock your daily basic credits and keep chatting."}
          </p>

          <div className="glm-actions">
            <button
              className="glm-btn glm-btn-primary"
              onClick={() => onNavigate("/login")}
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: "7px", verticalAlign: "middle" }}>
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                <polyline points="10 17 15 12 10 7" />
                <line x1="15" y1="12" x2="3" y2="12" />
              </svg>
              <span>{isMarathi ? "साइन इन करा (Sign In)" : "Sign In to Continue"}</span>
            </button>

            <button
              className="glm-btn glm-btn-outline"
              onClick={() => onNavigate("/signup")}
            >
              {isMarathi ? "नवीन खाते तयार करा (Sign Up)" : "Don't have an account? Sign Up"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// POPUP 5 — Credits Exhausted (logged-in user ran out)
// ══════════════════════════════════════════════════════════════════════════════

export function CreditsExhaustedPopup({ message, onClose, onNavigate, nextResetAt, lastDailyCreditReset }) {
  const renewalText = formatRenewalTime(nextResetAt, lastDailyCreditReset);

  return (
    <div
      className="cem-overlay"
      onPointerDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="cem-card" role="dialog" aria-modal="true" aria-labelledby="cem-title">

        <button className="cem-close" onClick={onClose} aria-label="Close dialog">
          <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="1" y1="1" x2="13" y2="13" />
            <line x1="13" y1="1" x2="1" y2="13" />
          </svg>
        </button>

        <div className="cem-glow-fx" aria-hidden="true" />

        <div className="cem-banner">
          <div className="cem-icon-wrapper">
            <div className="cem-icon-glow" aria-hidden="true" />
            <div className="cem-icon-badge">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="5" width="20" height="14" rx="3" />
                <line x1="2" y1="10" x2="22" y2="10" />
                <line x1="6" y1="15" x2="10" y2="15" />
              </svg>
            </div>
          </div>
          <h2 id="cem-title" className="cem-title">Credits Exhausted</h2>
        </div>

        <div className="cem-body">
          <p className="cem-text">
            {message || "Your daily credits have run out. Upgrade your plan to get more credits or wait for your daily renewal."}
          </p>

          {Boolean(renewalText && renewalText.trim().length > 0) && (
            <div className="cem-renewal-badge">
              <svg className="cem-renewal-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span>{renewalText}</span>
            </div>
          )}

          <div className="cem-actions">
            <button
              className="cem-btn cem-btn-primary"
              onClick={() => onNavigate("/subscription")}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
              <span>Upgrade Plan</span>
            </button>

            <button className="cem-btn cem-btn-outline" onClick={onClose}>
              Do it Later
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}